/* eslint-disable no-case-declarations */
import { useEffect, useState } from 'react';
import {
	Box,
	Pagination,
	PaginationItem,
	Stack,
	Switch,
	TableCell,
	TableRow,
	Typography,
	useTheme,
} from '@mui/material';
import { ButtonComponent } from 'components/ButtonComponent';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { TableComponent } from 'components/TableComponent';
import { createCoinsCouples, getAllCoinsCouples, updateCoinsCouples } from 'services/plans-business-model.service';
import { ICoinCouple, ICurrency } from 'constants/business-plan.constants';
import { formatDate } from 'constants/global.constants';
import {
	getAddNewCoinsCouple,
	getCoinsCouples,
	isLoading,
	setAddNewCoinsCouple,
	setError,
} from 'state/reducers/plans-business-model.reducers';
import { CustomSwitch } from 'components/Inputs';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { SelectList } from 'components/SelectList';
import { getCurrencies } from 'state/reducers/global.reducer';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { tableHeaderTypeEnum } from 'constants/global.constants';
import { permissionEnums } from 'constants/permission.constants';
import { hasPermission } from 'components/AuthorizationComponent';

// eslint-disable-next-line complexity
export const CoinsCouples = () => {
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const coinsCouples = useAppSelector(getCoinsCouples);
	const [coinsCouplesToShow, setCoinsCouplesToShow] = useState<any>([]);
	const loading = useAppSelector(isLoading);
	const currencies = useAppSelector(getCurrencies);
	const addNewCoinsCouple = useAppSelector(getAddNewCoinsCouple);
	const coinsTableHeaderItems: any = [
		{ text: `${t('page.businessPlans.coinsCouples.table.headers.date')}`, sortType: tableHeaderTypeEnum.DATE },
		{
			text: `${t('page.businessPlans.coinsCouples.table.headers.sellingCoin')}`,
			sortType: tableHeaderTypeEnum.STRING,
			sortingPointer: 'sourceCurrency',
		},
		{
			text: `${t('page.businessPlans.coinsCouples.table.headers.buyingCoin')}`,
			sortType: tableHeaderTypeEnum.STRING,
			sortingPointer: 'targetCurrency',
		},
		{
			text: `${t('page.businessPlans.coinsCouples.table.headers.status')}`,
			sortType: tableHeaderTypeEnum.BOOLEAN,
		},
		'',
	];
	const [newCoinsCoupleActive, setNewCoinsCoupleActive] = useState(false);
	const [newCoinsCoupleSourceCurrency, setNewCoinsCoupleSourceCurrency] = useState<any>(null);
	const [newCoinsCoupleTargetCurrency, setNewCoinsCoupleTargetCurrency] = useState<any>(null);
	const [currenciesOptions, setCurrenciesOptions] = useState<any>([]);
	const limitResultsPerPage = 8;
	const [page, setPage] = useState(1);
	const rowsPerPage = 8;
	const [showActiveFirst, setShowActiveFirst] = useState(false);
	const [showAscendingNames, setShowAscendingNames] = useState(false);
	const [showAscendingTime, setShowAscendingTime] = useState(false);
	const coinsCouplesEditPermissions = hasPermission([
		permissionEnums.BUSINESS_MODEL_COINS_COUPLES_EDITOR,
		permissionEnums.BUSINESS_MODEL_CONTENT,
	]);
	const [activateSaveButton, setActivateSaveButton] = useState(false);

	useEffect(() => {
		dispatch(setAddNewCoinsCouple(false));
		setNewCoinsCoupleSourceCurrency(null);
		setNewCoinsCoupleTargetCurrency(null);
	}, []);

	useEffect(() => {
		if (coinsCouplesEditPermissions) {
			dispatch(getAllCoinsCouples());
		}
	}, [coinsCouplesEditPermissions]);

	useEffect(() => {
		if (coinsCouples?.length > 0) {
			const uniqueCouples = coinsCouples.reduce((array: ICoinCouple[], coinCouple: ICoinCouple) => {
				if (
					!array.filter(
						(item: ICoinCouple) =>
							item.sourceCurrencyId === coinCouple.sourceCurrencyId &&
							item.targetCurrencyId === coinCouple.targetCurrencyId
					).length
				) {
					array.push(coinCouple);
				}
				return array;
			}, []);
			setCoinsCouplesToShow(uniqueCouples);
		}
	}, [coinsCouples]);

	useEffect(() => {
		if (currencies) {
			const options = currencies.map((currency: ICurrency) => {
				return { value: currency.id, name: `${currency.code} ${currency.symbol}` };
			});
			setCurrenciesOptions(options);
		}
	}, [currencies]);

	useEffect(() => {
		if (newCoinsCoupleSourceCurrency && newCoinsCoupleTargetCurrency) {
			const coupleExists = coinsCouples.some((couple: ICoinCouple) => {
				return (
					couple.sourceCurrency.id === newCoinsCoupleSourceCurrency.value &&
					couple.targetCurrency.id === newCoinsCoupleTargetCurrency.value
				);
			});
			if (coupleExists || newCoinsCoupleSourceCurrency.value === newCoinsCoupleTargetCurrency.value) {
				dispatch(setError(t('page.businessPlans.coinsCouples.duplicateMessage.title')));
				setActivateSaveButton(false);
			} else {
				setActivateSaveButton(true);
			}
		}
	}, [newCoinsCoupleSourceCurrency, newCoinsCoupleTargetCurrency]);

	const handleSwitchChange = (index: number, coinsCouple: ICoinCouple) => {
		const updatedCoinsCouple = { ...coinsCouple, isActive: !coinsCouple.isActive };
		coinsCouplesToShow[index] = updatedCoinsCouple;
		dispatch(
			updateCoinsCouples(
				{
					sourceCurrencyId: coinsCouple.sourceCurrency.id,
					targetCurrencyId: coinsCouple.targetCurrency.id,
					isActive: coinsCouple.isActive,
				},
				t('page.businessPlans.serverError')
			)
		);
	};

	const handleSaveNewCoinsCouple = () => {
		setNewCoinsCoupleSourceCurrency(null);
		setNewCoinsCoupleTargetCurrency(null);
		setNewCoinsCoupleActive(false);
		setActivateSaveButton(false);
		dispatch(
			createCoinsCouples(
				{
					sourceCurrencyId: newCoinsCoupleSourceCurrency.value,
					targetCurrencyId: newCoinsCoupleTargetCurrency.value,
					isActive: newCoinsCoupleActive,
				},
				t('page.businessPlans.serverError')
			)
		);
	};

	const handleChangeStatusOfNewCouple = () => {
		setNewCoinsCoupleActive(!newCoinsCoupleActive);
	};

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(page > newPage ? page - 1 : page + 1);
	};

	const sortByStatus = () => {
		setShowAscendingNames(false);
		setShowAscendingTime(false);
		setCoinsCouplesToShow([
			...coinsCouplesToShow.filter((coinsCouple: ICoinCouple) =>
				showActiveFirst ? coinsCouple.isActive : !coinsCouple.isActive
			),
			...coinsCouplesToShow.filter((coinsCouple: ICoinCouple) =>
				showActiveFirst ? !coinsCouple.isActive : coinsCouple.isActive
			),
		]);
		setShowActiveFirst(!showActiveFirst);
	};

	const sortByString = (sortingPointer: string) => {
		setShowActiveFirst(false);
		setShowAscendingTime(false);
		setCoinsCouplesToShow(
			coinsCouplesToShow.sort((a: any, b: any) =>
				showAscendingNames
					? b[`${sortingPointer}`].code.localeCompare(a[`${sortingPointer}`].code)
					: a[`${sortingPointer}`].code.localeCompare(b[`${sortingPointer}`].code)
			)
		);
		setShowAscendingNames(!showAscendingNames);
	};

	const sortByDate = () => {
		setShowActiveFirst(false);
		setShowAscendingNames(false);
		setShowAscendingNames(
			coinsCouplesToShow.sort((a: any, b: any) =>
				showAscendingTime
					? Date.parse(b.createdAt) - Date.parse(a.createdAt)
					: Date.parse(a.createdAt) - Date.parse(b.createdAt)
			)
		);
		setShowAscendingTime(!showAscendingTime);
	};

	const sortTable = (headerIndex: number) => {
		switch (coinsTableHeaderItems[headerIndex].sortType) {
			case tableHeaderTypeEnum.BOOLEAN:
				sortByStatus();
				break;
			case tableHeaderTypeEnum.STRING:
				sortByString(coinsTableHeaderItems[headerIndex].sortingPointer);
				break;
			case tableHeaderTypeEnum.DATE:
				sortByDate();
				break;
			default:
				break;
		}
	};

	return (
		<>
			<Stack spacing={2}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
					<Typography variant="h4" sx={{ fontWeight: '700', color: 'black', textTransform: 'capitalize' }}>
						{`${t('page.businessPlans.coinsCouples.coinsCouplesTitle')}`}
					</Typography>
					{coinsCouplesEditPermissions && (
						<ButtonComponent
							disabled={loading || addNewCoinsCouple}
							handleClick={() => dispatch(setAddNewCoinsCouple(true))}
							text={`${t('page.businessPlans.coinsCouples.addButton')}`}
							icon={
								<img
									src={'/icons/plus-sign.svg'}
									style={{
										marginLeft: '10px',
										filter: addNewCoinsCouple ? 'brightness(0.2)' : 'unset',
										opacity: addNewCoinsCouple ? 0.3 : 1,
									}}
								/>
							}
							styleObj={{ flexDirection: 'row-reverse' }}
						/>
					)}
				</Box>
				<TableComponent
					tableHeaderItems={coinsTableHeaderItems.map((header: any) => header.text)}
					handleClick={sortTable}
				>
					{addNewCoinsCouple && (
						<TableRow sx={{ background: theme.palette.root.lightGreen }}>
							<TableCell sx={{ width: '22%' }}>
								<Typography variant="body">{formatDate(new Date().toISOString())}</Typography>
							</TableCell>
							<TableCell sx={{ width: '22%' }}>
								<SelectList
									options={currenciesOptions}
									selected={newCoinsCoupleSourceCurrency}
									handleChange={(event: any) => {
										setNewCoinsCoupleSourceCurrency(event.target);
									}}
									styleObj={{ width: '120px' }}
								/>
							</TableCell>
							<TableCell sx={{ width: '22%' }}>
								<SelectList
									options={currenciesOptions}
									selected={newCoinsCoupleTargetCurrency}
									handleChange={(event: any) => {
										setNewCoinsCoupleTargetCurrency(event.target);
									}}
									styleObj={{ width: '120px' }}
								/>
							</TableCell>
							<TableCell sx={{ width: '22%' }}>
								<CustomSwitch checked={newCoinsCoupleActive ? true : false} theme={theme}>
									<Switch
										checked={newCoinsCoupleActive}
										onChange={
											coinsCouplesEditPermissions
												? () => handleChangeStatusOfNewCouple()
												: undefined
										}
									/>
								</CustomSwitch>
							</TableCell>
							<TableCell sx={{ width: '12%' }} align="center">
								<ButtonComponent
									disabled={loading || !activateSaveButton}
									handleClick={() => handleSaveNewCoinsCouple()}
									text={`${t('page.businessPlans.coinsCouples.saveButton')}`}
								/>
							</TableCell>
						</TableRow>
					)}

					{coinsCouplesToShow?.length > 0 &&
						coinsCouplesToShow
							.slice(
								(page - 1 < 0 ? page : page - 1) * rowsPerPage,
								(page - 1 < 0 ? page : page - 1) * rowsPerPage + rowsPerPage
							)
							?.map((coinCouple: ICoinCouple, index: number) => {
								return (
									<TableRow key={index}>
										<TableCell sx={{ width: '22%' }}>
											<Typography variant="body">{formatDate(coinCouple?.createdAt)}</Typography>
										</TableCell>
										<TableCell sx={{ width: '22%' }}>
											<Typography variant="body">
												{coinCouple?.sourceCurrency?.code}
												{'  '}
												{coinCouple?.sourceCurrency?.symbol}
											</Typography>
										</TableCell>
										<TableCell sx={{ width: '22%' }}>
											<Typography variant="body">
												{coinCouple?.targetCurrency?.code}
												{'  '}
												{coinCouple?.targetCurrency?.symbol}
											</Typography>
										</TableCell>
										<TableCell sx={{ width: '22%' }}>
											<CustomSwitch checked={coinCouple.isActive ? true : false} theme={theme}>
												<Switch
													checked={coinCouple.isActive}
													onChange={
														coinsCouplesEditPermissions
															? () => {
																	handleSwitchChange(index, coinCouple);
															  }
															: undefined
													}
												/>
											</CustomSwitch>
										</TableCell>
										<TableCell sx={{ width: '12%' }}></TableCell>
									</TableRow>
								);
							})}
				</TableComponent>
				<Pagination
					page={page}
					shape="rounded"
					count={
						coinsCouplesToShow.length < limitResultsPerPage
							? 1
							: Number.isInteger(coinsCouplesToShow.length / limitResultsPerPage)
							? coinsCouplesToShow.length / limitResultsPerPage
							: Math.floor(coinsCouplesToShow.length / limitResultsPerPage) + 1
					}
					renderItem={(item) => (
						<PaginationItem components={{ previous: ArrowForward, next: ArrowBack }} {...item} />
					)}
					onChange={handleChangePage}
				/>
				{!coinsCouples?.length && (
					<Typography sx={{ textAlign: 'center' }}>{`${t(
						'page.businessPlans.coinsCouples.noCoinsCouplesMessage'
					)}`}</Typography>
				)}
			</Stack>
		</>
	);
};
